/**
 * ResolvedAccountConfigProvider
 *
 * Auth-gated resolver that loads the selected client account's config from
 * author-api and provides it to learner-facing preview surfaces via the shared
 * AccountConfigProvider. Mirrors ResolvedThemeProvider.
 *
 * Semantic guarantees:
 * - No config query fires before authentication or for the base/Emtrain context
 *   (the endpoint has no base variant and 400s on a null account id).
 * - Base context resolves immediately to the bundled Emtrain defaults.
 * - Client config loading and failure remain explicit so the language selector
 *   can stay usable during loading and disable after bounded retries fail.
 * - On client-context switch the query key changes, so a prior account's config
 *   is never carried forward.
 */
import { type ReactNode } from 'react';
import {
  AccountConfigProvider,
  DEFAULT_ACCOUNT_CONFIG,
} from '@course-authoring/shared-types';
import { useAuth } from '../auth/AuthContext';
import { useClientContext, toScopeAccountId } from '../hooks/useClientContext';
import { useAccountConfigQuery } from '../api/accountConfigApi';

interface ResolvedAccountConfigProviderProps {
  children: ReactNode;
}

export function ResolvedAccountConfigProvider({
  children,
}: ResolvedAccountConfigProviderProps) {
  const { isAuthenticated } = useAuth();
  const { clientContext } = useClientContext();
  const accountId = toScopeAccountId(clientContext?.id);
  const scopeKey = accountId === null ? 'default' : `client:${accountId}`;

  const shouldFetch = isAuthenticated && accountId !== null;
  const { data, isError } = useAccountConfigQuery(accountId, {
    enabled: shouldFetch,
  });
  const status = !shouldFetch ? 'ready' : isError ? 'error' : data ? 'ready' : 'loading';

  return (
    <AccountConfigProvider config={data ?? DEFAULT_ACCOUNT_CONFIG} status={status} scopeKey={scopeKey}>
      {children}
    </AccountConfigProvider>
  );
}
